import { Tooltip } from 'bootstrap'

// Floor-plan icons are torn down and rebuilt on every store-driven re-render. Bootstrap
// tooltips default to `animation: true`, which schedules an async transition callback
// (executeAfterTransition -> setTimeout -> _isWithActiveTrigger). When an animated
// tooltip's element is removed mid-hover during a re-render, that queued callback later
// fires against an instance whose `_activeTrigger` has been reset to null, throwing
// `Uncaught TypeError: can't convert null to object`. Creating these tooltips with
// animation disabled removes the transition callbacks entirely, so no stale async
// callback can fire after the element/instance is gone.
const TOOLTIP_OPTIONS = { animation: false }

/**
 * Import make_tooltip then pass class selector ('.device-tooltip') in js pack
 *
 * Idempotent: if an element already has a Tooltip instance attached, that instance is
 * reused instead of creating a second one. Bootstrap 5's `new Tooltip(el)` does NOT
 * dedupe — it creates a parallel instance with its own popper/listeners, so calling
 * make_tooltip multiple times on the same elements leaks event handlers.
 */
export function make_tooltip(class_selector) {
    const tooltips = document.querySelectorAll(class_selector);
    return [...tooltips].map(tooltip =>
        Tooltip.getInstance(tooltip) || new Tooltip(tooltip, {
            animation: false,
            fallbackPlacements: ['top'],
        })
    );
}

/**
 * Initialize Bootstrap tooltips for all tooltip elements
 *
 * Idempotent: skips elements that already have a Tooltip instance.
 *
 * Tooltips are created with `animation: false` so no transition callback is ever queued
 * for these elements. This is what prevents the `_isWithActiveTrigger` crash when an
 * icon (and its tooltip) is removed mid-hover during a floor-plan re-render.
 */
export function initializeTooltips(target) {
    if (!target || typeof target.querySelectorAll !== 'function') return

    const tooltipElements = target.querySelectorAll('[data-bs-toggle="tooltip"]')

    tooltipElements.forEach((element) => {
        if (Tooltip.getInstance(element)) return
        new Tooltip(element, TOOLTIP_OPTIONS)
    })
}

/**
 * Dispose Bootstrap tooltips for all tooltip elements within a target container.
 *
 * Tooltips are created via the ES module `Tooltip` import (see make_tooltip /
 * initializeTooltips). Under Shakapacker/Webpack, `window.bootstrap` is NOT defined,
 * so disposal MUST use this same module `Tooltip` reference to resolve instances —
 * not a global `bootstrap.Tooltip`.
 *
 * IMPORTANT: do NOT call `instance.hide()` before disposing. On an animated tooltip
 * `hide()` queues an async transition callback (_queueCallback -> setTimeout) and then
 * `dispose()` nulls the instance's internal state (including `_activeTrigger`); when the
 * queued callback later runs `_isWithActiveTrigger` it throws
 * `can't convert null to object`. We create tooltips with `animation: false` (so there is
 * no transition to await) and dispose directly. Disposal is wrapped in try/catch so a
 * detached element can't abort disposal of the remaining tooltips.
 *
 * No-op if the target is falsy or has no querySelectorAll.
 */
export function disposeTooltips(target) {
    if (!target || typeof target.querySelectorAll !== 'function') return

    const tooltipElements = target.querySelectorAll('[data-bs-toggle="tooltip"]')

    tooltipElements.forEach((element) => {
        disposeTooltip(element)
    })
}

/**
 * Dispose the Bootstrap tooltip instance attached to a SINGLE element.
 *
 * Used by the diff-based icon re-render path (floor_plan_icon_manager_controller): when a
 * single icon is removed (or its tooltip title changes and must be re-initialized), only
 * that one element's tooltip instance is disposed rather than every tooltip in the
 * container.
 *
 * Uses the SAME module `Tooltip` reference tooltips are created with (see
 * initializeTooltips) — NOT the global `bootstrap`, which is undefined under Shakapacker.
 * Follows the same "dispose directly, no hide()" rule as disposeTooltips and swallows
 * errors from an already-detached element so a stray failure can't break the reconcile.
 *
 * No-op if the element is falsy or has no tooltip instance.
 */
export function disposeTooltip(element) {
    if (!element) return

    const instance = Tooltip.getInstance(element)
    if (!instance) return

    try {
        instance.dispose()
    } catch (error) {
        // A tooltip whose element was already removed can throw during dispose; swallow.
    }
}
